import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import {
	Dialog,
	Form,
	FormItem,
	Toggle,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	getSettings,
	saveSettings,
	UserManagementWelcomeEmailSettingModel
} from "client/Api/UserManagement/userManagement";

type ManageWelcomeEmailDialogProps = {
	onClose: () => void;
};

export const ManageWelcomeEmailDialog = (props: ManageWelcomeEmailDialogProps): ReactElement => {
	const { onClose } = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<UserManagementWelcomeEmailSettingModel>(defaultFormData);
	const formHandler: IFormHandler<UserManagementWelcomeEmailSettingModel> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const model: UserManagementWelcomeEmailSettingModel = formData;

			try {
				await saveSettings(model, globalCancellation);

				alertManager.showSuccess(localize("_ChangesSaved"));

				setIsBusy(false);
				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_ChangesNotSaved"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			isBusy,
			onClose
		]
	);

	const stableOnClose: () => void = useStableImperativeCallback(onClose);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const userManagementSettingsModel: UserManagementWelcomeEmailSettingModel = await getSettings(signal);

					setFormData(userManagementSettingsModel);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, stableOnClose]
	);

	return (
		<Dialog
			hasBusyIndicator={isBusy}
			trackingId="manage-welcome-email-dialog"
		>
			<Dialog.Header
				title={localize("_ManageWelcomeEmail")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form
					onSubmit="NONE"
					hasWidthConstrained
					trackingId="welcome-email-form"
				>
					<FormItem>
						<Toggle
							{...formHandler.for("sendAutomaticWelcomeEmail").attachToCheckbox()}
							text={localize("_AutomaticallySendAWelcomeEmailWhenAddingImportingSyncingUsers")}
						/>

						<ValidationError {...formHandler.for("sendAutomaticWelcomeEmail").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Save")}
						onClick={saveHandler}
						trackingId="save"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ManageWelcomeEmailDialog.displayName = "ManageWelcomeEmailDialog";

const defaultFormData: UserManagementWelcomeEmailSettingModel = {
	sendAutomaticWelcomeEmail: false
};
